import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, map, switchMap, take } from 'rxjs';

import { taxFormsUrl } from './constants';
import { ApiCreds, rawTaxForms, TaxForms } from '@core/models';
import { EmployeeProfileService } from '@core/services';
import * as utils from '@core/services/utils/service.utils';

@Injectable({ providedIn: 'root' })
export class TaxFormsCollectionService {
  /*** Observable cache of clean  Tax Forms */
  taxFormsSubject = new BehaviorSubject<TaxForms>(null);
  taxForms$ = this.taxFormsSubject.asObservable();

  constructor(private employeeProfileService: EmployeeProfileService, private http: HttpClient) {
    /** Refresh Data for Tax Forms */
    this.refresh();
  }

  /** Fetch Data from API using Employee API Credentials */
  private fetchData(apiCreds: ApiCreds): Observable<rawTaxForms> {
    //* 1. Build URL string.
    const url = `${taxFormsUrl}/${apiCreds.bcl}/${apiCreds.id}`;
    //* 2. Fetch URL string and create Observable with Data.
    return this.http.get<rawTaxForms>(url);
  }

  private formatTaxForms(rawTaxForms: rawTaxForms): TaxForms {
    //* 1.
    const { Success, ErrorMessage, ...filteredData } = rawTaxForms;

    //* 2. sort tax Forms in *NEW* Array (avoiding mutation of original array) in descending order by {file year}.
    //? https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/sort

    const taxForms = [...filteredData.TaxFormInfos].sort((a, b) => b.FileYear - a.FileYear);

    //* 3 return camelCase taxForms
    return utils.keysToCamelCase(taxForms);
  }

  //* Fetch tax forms data from API.
  private fetchTaxForms(): Observable<TaxForms> {
    //* 1. Collect Employee Api Credentials from Profile Service.
    return this.employeeProfileService.apiCreds$.pipe(
      take(1),
      //* 2. SwitchMap to allow inner Observable & fetch Data with apiCreds
      switchMap(apiCreds => this.fetchData(apiCreds)),
      //* 3. Format data into clean Observable.
      map(rawTaxForms => this.formatTaxForms(rawTaxForms))
    );
  }

  refresh() {
    this.fetchTaxForms()
      .pipe(take(1))
      .subscribe(taxForms => this.taxFormsSubject.next(taxForms));
  }
}
